/**
 * Workflow v2 StepRail — left-rail navigator for the 9 workflow
 * steps (Step 0 upload + Steps 1-8 agent runs). Status is encoded
 * inside the step-number circle itself (icon + colour) rather than
 * via a trailing dot:
 *
 *   approved   → ✓ check, success green
 *   draft      → ! exclamation, warning amber (awaiting your review)
 *   generating → spinner, info blue (agent working)
 *   revising   → spinner, info blue (agent re-running after revise)
 *   failed     → ✕ cross, error red
 *   locked     → 🔒 lock icon, muted grey
 *   empty      → plain step number, neutral
 */
import React from 'react';
import { STEP_CATALOG } from '../state/workflowV2StepCatalog';
import { isStepLocked } from '../state/workflowV2Reducer';
import { Icon } from '../../../components/ui/Icon';
import styles from './WorkflowV2StepRail.module.css';

/** Pick the variant class + glyph for the step-number circle. */
function variantFor(status, locked) {
  if (locked) return { className: 'stepNumLocked', glyph: <Icon name="lock" size={12} /> };
  if (status === 'approved') return { className: 'stepNumApproved', glyph: <Icon name="check" size={14} /> };
  if (status === 'generating' || status === 'revising')
    return { className: 'stepNumInFlight', glyph: <Icon name="spinner" size={14} className={styles.spinnerSvg} /> };
  if (status === 'draft') return { className: 'stepNumDraft', glyph: <Icon name="warning" size={14} /> };
  if (status === 'failed') return { className: 'stepNumFailed', glyph: <Icon name="close" size={12} /> };
  return { className: '', glyph: null }; // empty — fall back to the step number
}

export default function WorkflowV2StepRail({ state, actions }) {
  return (
    <aside className={styles.rail}>
      <header className={styles.head}>
        <div className={styles.title}>TARA Workflow</div>
      </header>

      <ol className={styles.list}>
        {STEP_CATALOG.map((step) => {
          const stepState = state.steps[step.id];
          const locked = isStepLocked(state, step.id);
          const isCurrent = state.selectedStepId === step.id;
          const { className: variantClass, glyph } = variantFor(stepState.status, locked);

          return (
            <li key={step.id}>
              <button
                type="button"
                className={`${styles.item} ${isCurrent ? styles.itemActive : ''} ${locked ? styles.itemLocked : ''}`}
                onClick={locked ? undefined : () => actions.selectStep(step.id)}
                disabled={locked}
                title={locked ? 'Approve previous steps to unlock' : ''}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span className={`${styles.stepNum} ${variantClass ? styles[variantClass] : ''}`}>
                  {glyph ?? step.id}
                </span>
                <span className={styles.body}>
                  <span className={styles.label}>{step.title}</span>
                  <span className={styles.clause}>{step.clause}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>

    </aside>
  );
}
